var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AgentErrorType } from "healthex-common/agentstatus.dtos";
import { assertUserTypeOptional, checkUserType } from "healthex-common/checkusertype";
import { DataSourceHealthStatus, DataSourceType, } from "healthex-common/datawarehouse.dtos";
import { OrganizationDataDeliveryType, OrganizationUpdateFailureReason } from "healthex-common/organization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED, ParameterizedRemoteData, RemoteData } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { DataSourceWizardPage } from "./DataSourceWizardViewModel";
import { FormModel } from "./FormModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
export class AdminDataSettingsViewModel {
    constructor(container) {
        this.dataConnectionFormModel = new FormModel({
            agentURL: "",
            pairingCode: "",
            enableMonitoringAndLogging: true,
        }, ["agentURL", "pairingCode", "enableMonitoringAndLogging"]);
        this.dataAccessPermissionsFormModel = new FormModel({
            allowDataAccessForPatientIdentification: true,
            allowDataAccessForDataDelivery: true,
        }, []);
        this.dataDeliverySettingsFormModel = new FormModel({
            deliveryType: null,
            deliverUncompressedData: false,
            s3BucketName: "",
            s3AssumeRoleArn: "",
            s3BucketPath: "{orgId}/{externalId}/{healthexPatientId}/{dataType}/{projectId}/{timestamp}",
            sftpHost: "",
            sftpPort: null,
            sftpUsername: "",
            sftpPassword: "",
            sftpPath: "{orgId}/{externalId}/{healthexPatientId}/{dataType}/{projectId}/{timestamp}",
        }, ["s3BucketName", "s3AssumeRoleArn", "sftpHost", "sftpUsername", "sftpPassword"], (input) => {
            if (input.s3BucketPath !== undefined) {
                this.dataDeliverySettingsFormModel.clearError("s3BucketPath");
                if (input.s3BucketPath.trim().length > 0 && !validators.validDataDeliveryPath(input.s3BucketPath)) {
                    this.dataDeliverySettingsFormModel.addError("s3BucketPath", "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {dataType}, {projectId}, {timestamp}");
                }
            }
            if (input.sftpPath !== undefined) {
                this.dataDeliverySettingsFormModel.clearError("sftpPath");
                if (input.sftpPath.trim().length > 0 && !validators.validDataDeliveryPath(input.sftpPath)) {
                    this.dataDeliverySettingsFormModel.addError("sftpPath", "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {dataType}, {projectId}, {timestamp}");
                }
            }
        });
        this.awsDataDeliveryInstructionsDialog = new SimpleDialogModel();
        this.currentPage = DataSourceWizardPage.ADD_SOURCE;
        this.isEditingDataSource = false;
        this.currentDataSourceId = null;
        this.editingDataSources = false;
        this.editingDataConnection = false;
        this.editingDataAccessPermissions = false;
        this.editingDataDeliverySettings = false;
        this.dataConnectionValidated = false;
        this.agentStatusExpanded = false;
        this.agentUpgradeInProgress = false;
        this.agentUpgradeAvailable = false;
        this.agentPendingConfigDeployment = false;
        this.agentNeedsForceUpgrade = true;
        this.epicLogUploadStartDate = null;
        this.epicLogUploadEndDate = null;
        this.dataSourcePollingTimer = null;
        this.datasetsForDataSource = [];
        this.tablesForDataset = [];
        this.selectedTableIds = [];
        this.selectedDataSetId = null;
        this.agentUpgradeDialog = new SimpleDialogModel();
        this.agentTurnOffMonitoringDialog = new SimpleDialogModel();
        this.dataSourceAddDialog = new SimpleDialogModel();
        this.deleteDataSourceDialog = new SimpleDialogModel();
        this.epicLogUploadStartErrorMessage = null;
        this.epicLogUploadEndErrorMessage = null;
        this.organizationsManager = container.organizationsManager;
        this.bannerUIManager = container.bannerUIManager;
        this.provisioningService = container.provisioningService;
        this.invalidationManager = container.invalidationManager;
        this.adminUIManager = container.adminUIManager;
        this.deploymentService = container.deploymentService;
        this.organizationData = new ParameterizedRemoteData(this.invalidationManager, () => this.organizationId, () => this.loadOrganization());
        this.agentStatusData = new ParameterizedRemoteData(this.invalidationManager, () => this.healthcareOrganization, () => this.loadAgentStatus());
        this.agentUpgradeDetails = new ParameterizedRemoteData(this.invalidationManager, () => this.healthcareOrganization, () => this.loadAgentUpgradeDetails());
        this.agentConfigData = new ParameterizedRemoteData(this.invalidationManager, () => this.healthcareOrganization, () => this.loadAgentConfig());
        this.agentDeploymentConfigData = new ParameterizedRemoteData(this.invalidationManager, () => this.healthcareOrganization, () => this.loadAgentDeploymentConfig());
        this.dataSourcesData = new ParameterizedRemoteData(this.invalidationManager, () => this.organizationId, async () => {
            try {
                return await this.loadDataSources();
            }
            catch (error) {
                console.error("Failed to load data sources", error);
                return [];
            }
        });
        this.awsDataDeliveryInfoData = new RemoteData(this.invalidationManager, () => this.loadAWSDataDeliveryInfo());
        reaction(() => this.dataConnectionFormModel.input.agentURL, () => (this.dataConnectionValidated = false));
        reaction(() => this.dataConnectionFormModel.input.enableMonitoringAndLogging, async () => {
            var _a;
            if (this.agentDeploymentConfig === NOT_LOADED || !this.agentDeploymentConfig)
                return;
            if (!this.dataConnectionFormModel.input.enableMonitoringAndLogging &&
                ((_a = this.agentDeploymentConfig) === null || _a === void 0 ? void 0 : _a.monitoringEnabled) !== this.dataConnectionFormModel.input.enableMonitoringAndLogging) {
                const confirmed = await this.agentTurnOffMonitoringDialog.showAndWait();
                if (!confirmed) {
                    this.dataConnectionFormModel.input.enableMonitoringAndLogging = true;
                }
            }
        });
        reaction(() => this.adminUIManager.sectionToExpand, async () => {
            this.expandSectionIfNecessary();
        }, { fireImmediately: true });
        reaction(() => this.agentUpgradeInProgress, async () => {
            this.setTimerForUpgradePolling();
        });
        reaction(() => this.epicLogUploadStartDate, async () => {
            this.checkEpicLogUploadTimeRange();
        });
        reaction(() => this.epicLogUploadEndDate, async () => {
            this.checkEpicLogUploadTimeRange();
        });
        reaction(() => this.hasInProgressClassification, async (hasInProgress) => {
            if (hasInProgress) {
                this.setTimerForDataSourcePolling();
            }
        });
        reaction(() => this.dataDeliverySettingsFormModel.input.deliveryType, async () => {
            this.dataDeliverySettingsFormModel.clearErrors();
        });
    }
    stop() {
        if (this.dataSourcePollingTimer) {
            clearTimeout(this.dataSourcePollingTimer);
            this.dataSourcePollingTimer = null;
        }
    }
    isDataSourceClassifying(dataSourceId) {
        var _a;
        if (this.dataSources === NOT_LOADED)
            return false;
        const dataSource = this.dataSources.find((ds) => ds.id === dataSourceId);
        if (!dataSource)
            return false;
        return ((_a = dataSource.inProgressClassificationRunIds) !== null && _a !== void 0 ? _a : []).length > 0;
    }
    get dataSources() {
        return this.dataSourcesData.value;
    }
    get hasDataSources() {
        if (this.dataSources === NOT_LOADED)
            return false;
        return this.dataSources.length > 0;
    }
    get dataSourcesSummary() {
        var _a;
        if (this.dataSources === NOT_LOADED || this.agentStatus === NOT_LOADED)
            return "";
        const total = this.dataSources.length;
        const bigQueryCount = this.dataSources.filter((ds) => ds.type === DataSourceType.DATAWAREHOUSE_BIGQUERY).length;
        const snowflakeCount = this.dataSources.filter((ds) => ds.type === DataSourceType.DATAWAREHOUSE_SNOWFLAKE).length;
        const fhirCount = ((_a = this.agentStatus) === null || _a === void 0 ? void 0 : _a.overallStatus) === "OPERATIONAL" ? 1 : 0;
        const sources = [];
        if (bigQueryCount > 0)
            sources.push(`${bigQueryCount} BigQuery`);
        if (snowflakeCount > 0)
            sources.push(`${snowflakeCount} Snowflake`);
        if (fhirCount > 0)
            sources.push(`${fhirCount} FHIR`);
        const totalWithFhir = total + fhirCount;
        const breakdown = sources.length > 0 ? ` (${sources.join(", ")})` : "";
        return `${totalWithFhir} data source${totalWithFhir !== 1 ? "s" : ""}${breakdown} connected`;
    }
    get dialogTitle() {
        switch (this.currentPage) {
            case DataSourceWizardPage.ADD_SOURCE:
                return this.isEditingDataSource ? "Edit Data Source" : "Add Data Source";
            case DataSourceWizardPage.SELECT_DATASET:
                return "Select Dataset";
            case DataSourceWizardPage.SELECT_TABLES:
                return "Select Tables";
            default:
                return "";
        }
    }
    async openDataSourceRegistrationDialog(dataSourceId) {
        if (this.dataSources === NOT_LOADED)
            return;
        const datasource = this.dataSources.find((ds) => ds.id === dataSourceId);
        if (!datasource)
            return;
        this.currentPage =
            datasource.type === DataSourceType.DATAWAREHOUSE_BIGQUERY
                ? DataSourceWizardPage.SELECT_DATASET
                : DataSourceWizardPage.SELECT_TABLES;
        this.currentDataSourceId = dataSourceId;
        this.dataSourceAddDialog.open = true;
    }
    async editDataSource(id) {
        this.isEditingDataSource = true;
        this.currentDataSourceId = id;
        this.currentPage = DataSourceWizardPage.ADD_SOURCE;
        this.dataSourceAddDialog.open = true;
    }
    async onDelete(id) {
        if (!this.organizationId)
            return;
        const confirmed = await this.deleteDataSourceDialog.showAndWait();
        if (!confirmed)
            return;
        await this.provisioningService.deleteDataSource(this.organizationId, id);
        await this.dataSourcesData.waitForReload();
    }
    async loadDataSources() {
        if (!this.organizationId)
            return [];
        return await this.provisioningService.getDataSources(this.organizationId);
    }
    async openAddDataSourceModal() {
        this.currentPage = DataSourceWizardPage.ADD_SOURCE;
        this.isEditingDataSource = false;
        this.currentDataSourceId = null;
        if (this.editingDataSources) {
            this.dataSourceAddDialog.open = true;
        }
        else {
            this.editingDataSources = true;
            this.dataSourceAddDialog.open = true;
            void this.loadDataConnection();
        }
    }
    async closeWizard() {
        this.dataSourceAddDialog.onChoice(false);
        await this.dataSourcesData.waitForReload();
    }
    get organizationId() {
        return this.adminUIManager.organizationId;
    }
    get organization() {
        return this.organizationData.value;
    }
    get healthcareOrganization() {
        if (this.organizationData.value === NOT_LOADED)
            return NOT_LOADED;
        return this.organizationData.value ? checkUserType(this.organizationData.value, UserType.HEALTHCARE_ORGANIZATION) : null;
    }
    get agentStatus() {
        var _a;
        if (this.agentStatusData.value === NOT_LOADED)
            return NOT_LOADED;
        if (this.agentStatusData.value === null)
            return null;
        if (this.agentUpgradeData === NOT_LOADED)
            return NOT_LOADED;
        const errors = (_a = this.agentStatusData.value.errors) !== null && _a !== void 0 ? _a : [];
        const unreachable = errors.includes(AgentErrorType.AGENT_UNREACHABLE);
        return {
            overallStatus: unreachable || errors.length >= 3 || this.agentNeedsForceUpgrade
                ? "SERVICE_DISRUPTION"
                : errors.length > 0
                    ? "PARTIAL_SERVICE_DISRUPTION"
                    : "OPERATIONAL",
            agentUnreachable: unreachable,
            patientIdentificationError: unreachable || errors.includes(AgentErrorType.PATIENT_INGEST_FAILED),
            contactsError: unreachable || errors.includes(AgentErrorType.PATIENT_CONTACT_FETCH_FAILED),
            dataDeliveryError: unreachable || errors.includes(AgentErrorType.PATIENT_FULL_RECORD_FETCH_FAILED),
            version: this.agentStatusData.value.version,
        };
    }
    get agentUpgradeData() {
        if (this.agentUpgradeDetails.value === NOT_LOADED)
            return NOT_LOADED;
        if (this.agentUpgradeDetails.value === null)
            return null;
        return this.agentUpgradeDetails.value;
    }
    get agentUpgradeText() {
        if (this.agentNeedsForceUpgrade)
            return "Update needed to restore connection";
        if (this.agentPendingConfigDeployment)
            return "Updated configuration needs to be deployed";
        return "A new version is available";
    }
    get agentUpgradeProgressText() {
        return this.agentUpgradeInProgress ? "Updating data connection" : "Update now";
    }
    toggleAgentStatusExpanded() {
        this.agentStatusExpanded = !this.agentStatusExpanded;
    }
    get agentConfig() {
        return this.agentConfigData.value;
    }
    get agentDeploymentConfig() {
        return this.agentDeploymentConfigData.value;
    }
    setTimerForUpgradePolling() {
        if (this.agentUpgradeInProgress) {
            setTimeout(async () => {
                await this.loadAgentUpgradeDetails();
                this.setTimerForUpgradePolling();
            }, 60000);
        }
    }
    expandSectionIfNecessary() {
        if (this.adminUIManager.sectionToExpand === "CONNECTION") {
            this.toggleEditingDataConnection();
        }
        else if (this.adminUIManager.sectionToExpand === "ACCESS_PERMISSIONS") {
            this.toggleEditingDataAccessPermissions();
        }
        else if (this.adminUIManager.sectionToExpand === "DELIVERY") {
            this.toggleEditingDataDeliverySettings();
        }
    }
    async loadOrganization() {
        if (!this.organizationId)
            return null;
        return assertUserTypeOptional(await this.organizationsManager.loadOrganization(this.organizationId), UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
    }
    toggleEditingDataConnection() {
        this.editingDataConnection = !this.editingDataConnection;
        if (this.editingDataConnection) {
            void this.loadDataConnection();
        }
    }
    toggleEditingDataAccessPermissions() {
        this.editingDataAccessPermissions = !this.editingDataAccessPermissions;
        if (this.editingDataAccessPermissions) {
            void this.loadDataAccessPermissions();
        }
    }
    toggleEditingDataSources() {
        this.editingDataSources = !this.editingDataSources;
        if (this.editingDataSources) {
            void this.loadDataConnectionFromAgent();
        }
    }
    toggleEditingDataDeliverySettings() {
        this.editingDataDeliverySettings = !this.editingDataDeliverySettings;
        if (this.editingDataDeliverySettings) {
            void this.loadDataDeliverySettings();
        }
    }
    async loadDataConnection() {
        var _a, _b, _c, _d;
        await this.organizationData.waitForValue();
        this.dataConnectionFormModel.clearErrors();
        if (this.healthcareOrganization === NOT_LOADED)
            return;
        this.dataConnectionFormModel.updateInput({
            agentURL: (_b = (_a = this.healthcareOrganization) === null || _a === void 0 ? void 0 : _a.agentBaseUrl) !== null && _b !== void 0 ? _b : "",
            pairingCode: (_d = (_c = this.healthcareOrganization) === null || _c === void 0 ? void 0 : _c.pairingCode) !== null && _d !== void 0 ? _d : "",
        });
        if (this.dataConnectionFormModel.input.agentURL.length > 0) {
            await this.loadDataConnectionFromAgent();
        }
    }
    async loadDataConnectionFromAgent() {
        var _a, _b;
        await this.agentConfigData.waitForValue();
        await this.agentDeploymentConfigData.waitForValue();
        if (this.agentConfig === NOT_LOADED)
            return;
        if (this.agentDeploymentConfig === NOT_LOADED)
            return;
        this.dataConnectionFormModel.updateInput({
            enableMonitoringAndLogging: (_b = (_a = this.agentDeploymentConfig) === null || _a === void 0 ? void 0 : _a.monitoringEnabled) !== null && _b !== void 0 ? _b : true,
        });
        this.dataConnectionValidated = this.agentConfig !== null && this.agentDeploymentConfig !== null;
        if (this.agentConfig === null || this.agentDeploymentConfig === null) {
            this.dataConnectionFormModel.addError("agentURL", "Unable to fetch existing configuration from Secure Data Gateway");
        }
    }
    async loadDataDeliverySettings() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13;
        await this.organizationData.waitForValue();
        this.dataDeliverySettingsFormModel.clearErrors();
        if (this.organization === NOT_LOADED)
            return;
        const defaultPath = "{orgId}/{externalId}/{healthexPatientId}/{dataType}/{projectId}/{timestamp}";
        this.dataDeliverySettingsFormModel.updateInput({
            deliveryType: (_c = (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.dataDeliverySettings) === null || _b === void 0 ? void 0 : _b.deliveryType) !== null && _c !== void 0 ? _c : null,
            deliverUncompressedData: (_f = (_e = (_d = this.organization) === null || _d === void 0 ? void 0 : _d.dataDeliverySettings) === null || _e === void 0 ? void 0 : _e.deliverUncompressedData) !== null && _f !== void 0 ? _f : false,
            s3BucketName: (_k = (_j = (_h = (_g = this.organization) === null || _g === void 0 ? void 0 : _g.dataDeliverySettings) === null || _h === void 0 ? void 0 : _h.s3Settings) === null || _j === void 0 ? void 0 : _j.s3BucketName) !== null && _k !== void 0 ? _k : "",
            s3AssumeRoleArn: (_p = (_o = (_m = (_l = this.organization) === null || _l === void 0 ? void 0 : _l.dataDeliverySettings) === null || _m === void 0 ? void 0 : _m.s3Settings) === null || _o === void 0 ? void 0 : _o.s3AssumeRoleArn) !== null && _p !== void 0 ? _p : "",
            s3BucketPath: (_t = (_s = (_r = (_q = this.organization) === null || _q === void 0 ? void 0 : _q.dataDeliverySettings) === null || _r === void 0 ? void 0 : _r.s3Settings) === null || _s === void 0 ? void 0 : _s.s3BucketPath) !== null && _t !== void 0 ? _t : defaultPath,
            sftpHost: (_x = (_w = (_v = (_u = this.organization) === null || _u === void 0 ? void 0 : _u.dataDeliverySettings) === null || _v === void 0 ? void 0 : _v.sftpSettings) === null || _w === void 0 ? void 0 : _w.sftpHost) !== null && _x !== void 0 ? _x : "",
            sftpPort: (_1 = (_0 = (_z = (_y = this.organization) === null || _y === void 0 ? void 0 : _y.dataDeliverySettings) === null || _z === void 0 ? void 0 : _z.sftpSettings) === null || _0 === void 0 ? void 0 : _0.sftpPort) !== null && _1 !== void 0 ? _1 : null,
            sftpUsername: (_5 = (_4 = (_3 = (_2 = this.organization) === null || _2 === void 0 ? void 0 : _2.dataDeliverySettings) === null || _3 === void 0 ? void 0 : _3.sftpSettings) === null || _4 === void 0 ? void 0 : _4.sftpUsername) !== null && _5 !== void 0 ? _5 : "",
            sftpPassword: (_9 = (_8 = (_7 = (_6 = this.organization) === null || _6 === void 0 ? void 0 : _6.dataDeliverySettings) === null || _7 === void 0 ? void 0 : _7.sftpSettings) === null || _8 === void 0 ? void 0 : _8.sftpPassword) !== null && _9 !== void 0 ? _9 : "",
            sftpPath: (_13 = (_12 = (_11 = (_10 = this.organization) === null || _10 === void 0 ? void 0 : _10.dataDeliverySettings) === null || _11 === void 0 ? void 0 : _11.sftpSettings) === null || _12 === void 0 ? void 0 : _12.sftpPath) !== null && _13 !== void 0 ? _13 : defaultPath,
        });
    }
    get agentConnected() {
        if (this.healthcareOrganization === NOT_LOADED)
            return NOT_LOADED;
        if (this.healthcareOrganization === null)
            return false;
        return this.healthcareOrganization.agentBaseUrl !== null && this.healthcareOrganization.agentBaseUrl.trim() !== "";
    }
    get displayAgentUpgrade() {
        if (this.healthcareOrganization === NOT_LOADED)
            return NOT_LOADED;
        if (this.healthcareOrganization === null) {
            throw Error("Unable to find org");
        }
        return !this.healthcareOrganization.agentIsHealthExManaged;
    }
    get agentIsHealthExManaged() {
        if (this.healthcareOrganization === NOT_LOADED)
            return false;
        if (this.healthcareOrganization === null)
            return false;
        return this.healthcareOrganization.agentIsHealthExManaged;
    }
    get showReviewDataConnectionBanner() {
        return this.agentConnected === false;
    }
    get supportedDataConnectionTypes() {
        return ["epic"];
    }
    getConnectionTypeDescription(ingestType) {
        if (ingestType === "epic")
            return "Epic FHIR";
        return "";
    }
    get showDataGatewayURLSection() {
        if (this.healthcareOrganization === NOT_LOADED || this.healthcareOrganization === null)
            return false;
        return !this.healthcareOrganization.agentIsHealthExManaged;
    }
    get showDataGatewayStatusSection() {
        if (this.healthcareOrganization === NOT_LOADED || this.healthcareOrganization === null)
            return false;
        return !this.showDataGatewayURLSection;
    }
    get showDataAccessPermissionsSection() {
        if (this.healthcareOrganization === NOT_LOADED || this.healthcareOrganization === null)
            return false;
        return true;
    }
    get showDataDeliverySettingsSection() {
        if (this.organization === NOT_LOADED || this.organization === null)
            return false;
        return this.organization.type === UserType.EXTERNAL_RESEARCHER;
    }
    get enableValidateURL() {
        return !this.dataConnectionValidated && this.dataConnectionFormModel.input.agentURL.length > 0;
    }
    get setupDataConnectionStepNumber() {
        return this.showDataGatewayURLSection ? 3 : 1;
    }
    get showEpicMonitoringAndLogging() {
        if (this.displayAgentUpgrade === NOT_LOADED)
            return false;
        return this.displayAgentUpgrade;
    }
    get epicUploadLogsUIEnabled() {
        if (this.displayAgentUpgrade === NOT_LOADED)
            return false;
        return !this.dataConnectionFormModel.input.enableMonitoringAndLogging;
    }
    get epicUploadLogsEnabled() {
        return this.epicUploadLogsUIEnabled && this.epicLogUploadTimeRangeIsValid;
    }
    get epicLogUploadTimeRangeIsValid() {
        if (!this.epicLogUploadStartDate || !this.epicLogUploadEndDate)
            return false;
        if (!!this.epicLogUploadStartErrorMessage || !!this.epicLogUploadEndErrorMessage)
            return false;
        return true;
    }
    async validateURL() {
        var _a;
        const { validURL } = validators;
        if (!validURL(this.dataConnectionFormModel.input.agentURL)) {
            console.log("agentURL error");
            this.dataConnectionFormModel.addError("agentURL", "Enter a valid URL");
            return;
        }
        const status = await this.loadAgentStatus(this.dataConnectionFormModel.input.agentURL);
        if (!status || ((_a = status.errors) === null || _a === void 0 ? void 0 : _a.includes(AgentErrorType.AGENT_UNREACHABLE))) {
            this.dataConnectionFormModel.addError("agentURL", "Unable to communicate with Secure Data Gateway");
            return;
        }
        this.dataConnectionFormModel.clearError("agentURL");
        this.dataConnectionValidated = true;
        await this.agentConfigData.waitForReload();
        await this.agentDeploymentConfigData.waitForReload();
        await this.loadDataConnectionFromAgent();
    }
    get dataConnectionSaveButtonText() {
        if (this.agentConnected === false) {
            return "Connect and save";
        }
        else {
            return "Save";
        }
    }
    async triggerAgentUpgrade() {
        if (this.healthcareOrganization && this.healthcareOrganization !== NOT_LOADED) {
            const confirmed = await this.agentUpgradeDialog.showAndWait();
            if (!confirmed)
                return;
            await this.deploymentService.triggerAgentUpgrade(this.healthcareOrganization.id);
            this.agentUpgradeInProgress = true;
        }
    }
    checkEpicLogUploadTimeRange() {
        const start = this.epicLogUploadStartDate;
        const end = this.epicLogUploadEndDate;
        let startError = null;
        let endError = null;
        const now = new Date();
        if (start !== null) {
            if (Number.isNaN(start.getTime())) {
                startError = "Invalid date";
            }
            else if (start.getTime() > now.getTime()) {
                startError = "Start date can not be in the future";
            }
        }
        if (end !== null) {
            if (Number.isNaN(end.getTime())) {
                endError = "Invalid date";
            }
        }
        if (start && end && !startError && !endError) {
            if (start.getTime() >= end.getTime()) {
                endError = "End date must be later than start date";
            }
        }
        this.epicLogUploadStartErrorMessage = startError;
        this.epicLogUploadEndErrorMessage = endError;
    }
    async uploadLogs() {
        if (!this.organizationId) {
            console.warn("Should not get here: no organization");
            return;
        }
        if (!this.epicLogUploadTimeRangeIsValid || !this.epicLogUploadStartDate || !this.epicLogUploadEndDate) {
            console.warn("Should not get here: invalid time range");
            return;
        }
        try {
            await this.deploymentService.triggerAgentLogUpload(this.organizationId, {
                fromTimestampUTC: this.epicLogUploadStartDate.toUTCString(),
                toTimestampUTC: this.epicLogUploadEndDate.toUTCString(),
                timeWindowMinutes: 2,
                batchSize: 100,
            });
            this.bannerUIManager.displayBanner({ text: "Logs uploaded successfully" });
        }
        catch (error) {
            this.bannerUIManager.displayBanner({ type: "ERROR", text: "Unexpected error uploading logs" });
        }
    }
    async connectAndSave() {
        if (!this.organizationId)
            return;
        this.dataConnectionFormModel.clearErrors();
        if (this.dataConnectionFormModel.hasErrors())
            return;
        const updateFailure = await this.organizationsManager.updateOrganizationDetails(this.organizationId, {
            type: UserType.HEALTHCARE_ORGANIZATION,
            agentBaseUrl: this.dataConnectionFormModel.input.agentURL,
            pairingCode: this.dataConnectionFormModel.input.pairingCode,
        });
        if (updateFailure) {
            if (updateFailure === OrganizationUpdateFailureReason.INVALID_PAIRING_CODE)
                this.dataConnectionFormModel.addError("pairingCode", "Invalid pairing code");
            return;
        }
        try {
            await this.provisioningService.updateAgentDeploymentConfig(this.organizationId, {
                monitoringEnabled: this.dataConnectionFormModel.input.enableMonitoringAndLogging,
            });
        }
        catch (e) {
            console.warn(`Unexpected error: ${JSON.stringify(e)}`);
            throw e;
        }
        await this.organizationData.waitForReload();
        await this.agentStatusData.waitForReload();
        await this.agentConfigData.waitForReload();
        await this.agentDeploymentConfigData.waitForReload();
        if (this.dataConnectionFormModel.input.agentURL) {
            this.adminUIManager.openGoLiveChecklistDialogModel();
        }
        this.editingDataConnection = false;
    }
    showCopySuccess(successMessage) {
        this.bannerUIManager.displayBanner({ text: successMessage });
    }
    async loadAgentStatus(overrideAgentBaseUrl) {
        if (this.healthcareOrganization &&
            this.healthcareOrganization !== NOT_LOADED &&
            (!!overrideAgentBaseUrl || !!this.healthcareOrganization.agentBaseUrl)) {
            return await this.provisioningService.getAgentStatus(this.healthcareOrganization.id, overrideAgentBaseUrl);
        }
        else {
            return null;
        }
    }
    async loadAgentUpgradeDetails() {
        if (this.healthcareOrganization && this.healthcareOrganization !== NOT_LOADED) {
            try {
                const upgradeDetails = await this.deploymentService.getAgentUpgradeDetails(this.healthcareOrganization.id);
                this.agentUpgradeInProgress = upgradeDetails.isUpgradeInProgress;
                this.agentUpgradeAvailable = upgradeDetails.isUpgradeAvailable;
                this.agentNeedsForceUpgrade = upgradeDetails.needToForceUpgrade;
                this.agentPendingConfigDeployment = upgradeDetails.pendingConfigRollout;
                return upgradeDetails;
            }
            catch (e) {
                console.log("Error while loading agent upgrade details", e);
                return null;
            }
        }
        else {
            return null;
        }
    }
    async loadAgentConfig() {
        if (this.healthcareOrganization && this.healthcareOrganization !== NOT_LOADED) {
            try {
                return await this.provisioningService.getAgentConfig(this.healthcareOrganization.id, this.dataConnectionFormModel.input.agentURL);
            }
            catch (e) {
                console.log("Error while loading agent config", e);
                return null;
            }
        }
        else {
            return null;
        }
    }
    async loadAgentDeploymentConfig() {
        if (this.healthcareOrganization && this.healthcareOrganization !== NOT_LOADED) {
            try {
                return await this.provisioningService.getAgentDeploymentConfig(this.healthcareOrganization.id);
            }
            catch (e) {
                console.log("Error while loading agent deployment config", e);
                return null;
            }
        }
        else {
            return null;
        }
    }
    async loadAWSDataDeliveryInfo() {
        return await this.provisioningService.getAWSDataDeliveryInfo();
    }
    get agentManagedBy() {
        var _a, _b;
        if (this.healthcareOrganization === NOT_LOADED)
            return null;
        return ((_a = this.healthcareOrganization) === null || _a === void 0 ? void 0 : _a.agentIsHealthExManaged) ? "HealthEx" : (_b = this.healthcareOrganization) === null || _b === void 0 ? void 0 : _b.name;
    }
    get dataAccessPermissionsDescription() {
        var _a, _b, _c, _d;
        if (this.healthcareOrganization === NOT_LOADED)
            return null;
        if (((_a = this.healthcareOrganization) === null || _a === void 0 ? void 0 : _a.allowDataAccessForPatientIdentification) &&
            ((_b = this.healthcareOrganization) === null || _b === void 0 ? void 0 : _b.allowDataAccessForDataDelivery)) {
            return "Your organization currently allows data access for both patient identification and data delivery.";
        }
        else if ((_c = this.healthcareOrganization) === null || _c === void 0 ? void 0 : _c.allowDataAccessForPatientIdentification) {
            return "Your organization currently only allows data access for patient identification.";
        }
        else if ((_d = this.healthcareOrganization) === null || _d === void 0 ? void 0 : _d.allowDataAccessForDataDelivery) {
            return "Your organization currently only allows data access for data delivery.";
        }
        else {
            return "Your organization currently does not allow data access for either patient identification or data delivery.";
        }
    }
    get dataDeliverySettingsDescription() {
        var _a, _b, _c, _d;
        if (this.organization === NOT_LOADED)
            return null;
        if (((_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.dataDeliverySettings) === null || _b === void 0 ? void 0 : _b.deliveryType) === OrganizationDataDeliveryType.S3) {
            return "Your organization currently uses S3 for data delivery.";
        }
        else if (((_d = (_c = this.organization) === null || _c === void 0 ? void 0 : _c.dataDeliverySettings) === null || _d === void 0 ? void 0 : _d.deliveryType) === OrganizationDataDeliveryType.SFTP) {
            return "Your organization currently uses SFTP for data delivery.";
        }
        else {
            return "Your organization has not configured a data delivery method.";
        }
    }
    async loadDataAccessPermissions() {
        var _a, _b, _c, _d;
        await this.organizationData.waitForValue();
        if (this.healthcareOrganization === NOT_LOADED)
            return;
        this.dataAccessPermissionsFormModel.clearErrors();
        this.dataAccessPermissionsFormModel.updateInput({
            allowDataAccessForPatientIdentification: (_b = (_a = this.healthcareOrganization) === null || _a === void 0 ? void 0 : _a.allowDataAccessForPatientIdentification) !== null && _b !== void 0 ? _b : true,
            allowDataAccessForDataDelivery: (_d = (_c = this.healthcareOrganization) === null || _c === void 0 ? void 0 : _c.allowDataAccessForDataDelivery) !== null && _d !== void 0 ? _d : true,
        });
        if (this.dataConnectionFormModel.input.agentURL.length > 0) {
            await this.loadDataConnectionFromAgent();
        }
    }
    async saveDataAccessPermissions() {
        if (!this.organizationId)
            return;
        const { allowDataAccessForPatientIdentification, allowDataAccessForDataDelivery } = this.dataAccessPermissionsFormModel.input;
        await this.organizationsManager.updateOrganizationDetails(this.organizationId, {
            type: UserType.HEALTHCARE_ORGANIZATION,
            allowDataAccessForPatientIdentification,
            allowDataAccessForDataDelivery,
        });
        await this.organizationData.waitForReload();
        this.editingDataAccessPermissions = false;
    }
    restoreDataAccessPermissionDefaults() {
        this.dataAccessPermissionsFormModel.updateInput({
            allowDataAccessForPatientIdentification: true,
            allowDataAccessForDataDelivery: true,
        });
    }
    async saveDataDeliverySettings() {
        var _a, _b, _c, _d;
        if (this.organization === NOT_LOADED || !this.organization)
            return;
        this.dataDeliverySettingsFormModel.clearErrors();
        const wasNotConfigured = ((_b = (_a = this.organization.dataDeliverySettings) === null || _a === void 0 ? void 0 : _a.deliveryType) !== null && _b !== void 0 ? _b : null) === null;
        this.dataDeliverySettingsFormModel.validateField("deliveryType", validators.notNull, "Select a delivery type");
        if (this.dataDeliverySettingsFormModel.input.deliveryType === OrganizationDataDeliveryType.S3) {
            this.dataDeliverySettingsFormModel.validateField("s3BucketName", validators.notBlank, "Enter a bucket name");
            this.dataDeliverySettingsFormModel.validateField("s3AssumeRoleArn", validators.notBlank, "Enter an IAM role ARN");
            this.dataDeliverySettingsFormModel.validateField("s3AssumeRoleArn", validators.validIAMRoleARN, "Enter a valid IAM role ARN");
            this.dataDeliverySettingsFormModel.validateField("s3BucketPath", validators.validDataDeliveryPath, "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {dataType}, {projectId}, {timestamp}");
        }
        else if (this.dataDeliverySettingsFormModel.input.deliveryType === OrganizationDataDeliveryType.SFTP) {
            this.dataDeliverySettingsFormModel.validateField("sftpHost", validators.notBlank, "Enter a host");
            this.dataDeliverySettingsFormModel.validateField("sftpUsername", validators.notBlank, "Enter a username");
            this.dataDeliverySettingsFormModel.validateField("sftpPassword", validators.notBlank, "Enter a password");
            this.dataDeliverySettingsFormModel.validateField("sftpPath", validators.validDataDeliveryPath, "Path must only contain allowed parameters: {orgId}, {externalId}, {healthexPatientId}, {dataType}, {projectId}, {timestamp}");
        }
        if (this.dataDeliverySettingsFormModel.hasErrors())
            return;
        const { deliveryType, deliverUncompressedData, s3BucketName, s3AssumeRoleArn, s3BucketPath, sftpHost, sftpPort, sftpUsername, sftpPassword, sftpPath, } = this.dataDeliverySettingsFormModel.input;
        const nullIfEmpty = (value) => (value === "" ? null : value);
        await this.organizationsManager.updateOrganizationDetails(this.organization.id, {
            type: this.organization.type,
            dataDeliverySettings: {
                deliveryType,
                deliverUncompressedData,
                s3Settings: {
                    s3BucketName,
                    s3AssumeRoleArn,
                    s3BucketPath: nullIfEmpty(s3BucketPath),
                },
                sftpSettings: {
                    sftpHost,
                    sftpPort,
                    sftpUsername,
                    sftpPassword,
                    sftpPath: nullIfEmpty(sftpPath),
                },
            },
        });
        await this.organizationData.waitForReload();
        const nowConfigured = ((_d = (_c = this.organization.dataDeliverySettings) === null || _c === void 0 ? void 0 : _c.deliveryType) !== null && _d !== void 0 ? _d : null) !== null;
        this.editingDataDeliverySettings = false;
        if (wasNotConfigured && nowConfigured) {
            this.adminUIManager.openGoLiveChecklistDialogModel();
        }
    }
    get hasInProgressClassification() {
        if (this.dataSources === NOT_LOADED)
            return false;
        return this.dataSources.some((ds) => { var _a; return ((_a = ds.inProgressClassificationRunIds) !== null && _a !== void 0 ? _a : []).length > 0; });
    }
    setTimerForDataSourcePolling() {
        if (this.dataSourcePollingTimer) {
            clearTimeout(this.dataSourcePollingTimer);
            this.dataSourcePollingTimer = null;
        }
        if (this.hasInProgressClassification) {
            this.dataSourcePollingTimer = setTimeout(async () => {
                await this.dataSourcesData.waitForReload();
                this.setTimerForDataSourcePolling();
            }, 10000);
        }
    }
    get allDataSources() {
        var _a, _b, _c;
        if (this.dataSources === NOT_LOADED || this.agentConfig === NOT_LOADED) {
            return NOT_LOADED;
        }
        const warehouseSources = (_a = this.dataSources) !== null && _a !== void 0 ? _a : [];
        const fhirSource = this.agentConfigData.value !== NOT_LOADED && ((_b = this.agentConfigData.value) === null || _b === void 0 ? void 0 : _b.fhirEndpoint)
            ? [
                {
                    id: "fhir",
                    type: "FHIR",
                    name: "Epic FHIR",
                    healthStatus: this.agentStatus === NOT_LOADED
                        ? "UNKNOWN"
                        : ((_c = this.agentStatus) === null || _c === void 0 ? void 0 : _c.overallStatus) === DataSourceHealthStatus.OPERATIONAL
                            ? DataSourceHealthStatus.HEALTHY
                            : DataSourceHealthStatus.SERVICE_DISRUPTION,
                },
            ]
            : [];
        return [...warehouseSources, ...fhirSource];
    }
    get externalDataDeliveryRoleARN() {
        var _a;
        if (this.awsDataDeliveryInfoData.value === NOT_LOADED)
            return NOT_LOADED;
        return (_a = this.awsDataDeliveryInfoData.value.externalDataDeliveryRoleARN) !== null && _a !== void 0 ? _a : null;
    }
}
__decorate([
    observable,
    __metadata("design:type", String)
], AdminDataSettingsViewModel.prototype, "currentPage", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "isEditingDataSource", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "currentDataSourceId", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "editingDataSources", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "editingDataConnection", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "editingDataAccessPermissions", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "editingDataDeliverySettings", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "dataConnectionValidated", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "agentStatusExpanded", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "agentUpgradeInProgress", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "agentUpgradeAvailable", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "agentPendingConfigDeployment", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "agentNeedsForceUpgrade", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "epicLogUploadStartDate", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "epicLogUploadEndDate", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], AdminDataSettingsViewModel.prototype, "datasetsForDataSource", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], AdminDataSettingsViewModel.prototype, "tablesForDataset", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], AdminDataSettingsViewModel.prototype, "selectedTableIds", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "selectedDataSetId", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "dataSources", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "hasDataSources", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "dataSourcesSummary", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "organizationId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "organization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "healthcareOrganization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentStatus", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentUpgradeData", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentUpgradeText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentUpgradeProgressText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentConfig", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentDeploymentConfig", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentConnected", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "displayAgentUpgrade", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentIsHealthExManaged", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "showReviewDataConnectionBanner", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "supportedDataConnectionTypes", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "showDataGatewayURLSection", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "showDataGatewayStatusSection", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "showDataAccessPermissionsSection", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "enableValidateURL", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "setupDataConnectionStepNumber", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "showEpicMonitoringAndLogging", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "epicUploadLogsUIEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "epicUploadLogsEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "epicLogUploadTimeRangeIsValid", null);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "epicLogUploadStartErrorMessage", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AdminDataSettingsViewModel.prototype, "epicLogUploadEndErrorMessage", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "dataConnectionSaveButtonText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "agentManagedBy", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "dataAccessPermissionsDescription", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "dataDeliverySettingsDescription", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "hasInProgressClassification", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "allDataSources", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AdminDataSettingsViewModel.prototype, "externalDataDeliveryRoleARN", null);
